import React from 'react';
import TopNav from '../components/TopNav';
import Footer from '../components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Target, Mail, AlertCircle } from 'lucide-react';

export default function WeWantYou() {
  return (
    <div className="min-h-screen flex flex-col">
      <TopNav />
      
      <main className="flex-grow">
        <div className="bg-gradient-to-b from-muted/50 to-background py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <Badge className="mb-4" variant="secondary">Partnership Opportunity</Badge>
              <h1 className="text-5xl font-semibold tracking-tight mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Want to Be a Founder?
              </h1>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Partner with us and earn up to 40% equity in a business—without spending or investing any of your own capital.
              </p>
            </div>
            
            {/* Main Opportunity */}
            <Card className="rounded-xl mb-8 bg-gradient-to-br from-primary/5 to-background border-2" style={{ borderColor: 'var(--ap-blue)' }}>
              <CardContent className="pt-8 pb-8">
                <Target className="h-12 w-12 mx-auto mb-6" style={{ color: 'var(--ap-blue)' }} />
                <h2 className="text-2xl font-semibold mb-4 text-center" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  The Opportunity
                </h2>
                <div className="space-y-4 text-muted-foreground max-w-2xl mx-auto">
                  <p>
                    We are looking for highly motivated, success-driven individuals who have a strong desire to co-found and build their own business, without having to spend or invest any of their own capital.
                  </p>
                  <p>
                    We will either build you a business or give you one to run within our portfolio, and you will have the opportunity to own free and clear up to 40%.
                  </p>
                </div>
              </CardContent>
            </Card>
            
            {/* What You'll Receive */}
            <Card className="rounded-xl mb-8">
              <CardHeader>
                <CardTitle className="text-2xl">What You'll Receive</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-muted-foreground">
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full mt-2 flex-shrink-0" style={{ backgroundColor: 'var(--ap-blue)' }}></div>
                  <p>Hands-on training in building and scaling automated businesses</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full mt-2 flex-shrink-0" style={{ backgroundColor: 'var(--ap-blue)' }}></div>
                  <p>Real equity stake in a business that you co-founded</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full mt-2 flex-shrink-0" style={{ backgroundColor: 'var(--ap-blue)' }}></div>
                  <p>Ongoing support from our team</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full mt-2 flex-shrink-0" style={{ backgroundColor: 'var(--ap-blue)' }}></div>
                  <p>Strength of our portfolio and network</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full mt-2 flex-shrink-0" style={{ backgroundColor: 'var(--ap-blue)' }}></div>
                  <p>Knowledge and expertise in AI/Automation</p>
                </div>
              </CardContent>
            </Card>
            
            {/* Equity Structure Example */}
            <Card className="rounded-xl mb-8 bg-muted/30">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <CardTitle className="text-xl">Equity Structure (Example - Draft)</CardTitle>
                  <Badge variant="outline" className="text-xs">Example Only</Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 text-sm">
                  <div className="flex items-center justify-between p-3 bg-background rounded-lg">
                    <span className="text-muted-foreground">Upon Completion:</span>
                    <span className="font-semibold">Equity Issued</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-background rounded-lg">
                    <span className="text-muted-foreground">First Revenue Milestone:</span>
                    <span className="font-semibold">Equity Issued</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-background rounded-lg">
                    <span className="text-muted-foreground">Cash Flow Positive:</span>
                    <span className="font-semibold">Equity Issued</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-background rounded-lg">
                    <span className="text-muted-foreground">Final Milestone:</span>
                    <span className="font-semibold">Equity Issued</span>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-4 italic">
                  Note: This is a draft example. Actual equity structure will be determined on a case-by-case basis and documented in formal agreements.
                </p>
              </CardContent>
            </Card>
            
            {/* Important Requirements */}
            <Card className="rounded-xl mb-8 border-2 border-orange-200 dark:border-orange-900 bg-orange-50/50 dark:bg-orange-950/20">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <AlertCircle className="h-5 w-5 text-orange-600 dark:text-orange-500" />
                  <CardTitle className="text-xl">Important Requirements</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-3 text-sm text-muted-foreground">
                <p className="font-semibold text-foreground">Please read carefully before applying:</p>
                <ul className="space-y-2 ml-4">
                  <li>• You must be okay not making any money for the first few months</li>
                  <li>• You are an owner and will not initially be receiving a salary</li>
                  <li>• We can work out some sort of draw to keep you afloat based on sales/earnings on a case-by-case basis</li>
                  <li>• No degree necessary - we don't care if you have a PhD or just a high school diploma</li>
                  <li>• However: Do not apply if you started and dropped out of any post-high school education program without completing it</li>
                </ul>
              </CardContent>
            </Card>
            
            {/* How to Apply */}
            <Card className="rounded-xl bg-gradient-to-br from-primary/10 to-background border-2" style={{ borderColor: 'var(--ap-blue)' }}>
              <CardContent className="pt-8 pb-8">
                <Mail className="h-12 w-12 mx-auto mb-4" style={{ color: 'var(--ap-blue)' }} />
                <h3 className="text-2xl font-semibold mb-4 text-center" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  How to Apply
                </h3>
                
                <div className="max-w-2xl mx-auto space-y-4 mb-6">
                  <p className="text-muted-foreground text-center">
                    If you are interested, please send an email to:
                  </p>
                  <p className="text-center">
                    <a href="mailto:adoucet@automatedprime.com" className="text-xl font-semibold hover:underline" style={{ color: 'var(--ap-blue)' }}>
                      adoucet@automatedprime.com
                    </a>
                  </p>
                  
                  <Card className="bg-background">
                    <CardContent className="pt-6">
                      <p className="font-semibold mb-4 text-center">What We Care About</p>
                      <div className="space-y-3 text-sm text-muted-foreground">
                        <p className="font-semibold text-foreground">Do NOT send me your resume.</p>
                        <p>I am not concerned about your past experience or lack thereof. I will teach you how to do this.</p>
                        <p className="font-semibold text-foreground">All I am concerned with and care about in an applicant is their attitude, and desire/willingness to succeed and win at all costs.</p>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <div className="bg-muted/50 rounded-lg p-6">
                    <p className="font-semibold mb-4">Include answers to these 4 questions in your email:</p>
                    <ol className="space-y-3 text-sm text-muted-foreground">
                      <li><strong className="text-foreground">1.</strong> What motivates you to succeed?</li>
                      <li><strong className="text-foreground">2.</strong> What do you think your greatest success has been so far in life?</li>
                      <li><strong className="text-foreground">3.</strong> What has been your most impactful mistake?</li>
                      <li><strong className="text-foreground">4.</strong> How do you feel when people around you succeed?</li>
                    </ol>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
}
